import type { FC } from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import type { IResultStatus, IServiceError, IUserCommunication } from "@/Components/Api/CommonServiceOutput";
import { sendTelemetryEvent } from "@/lib/telemetry";
import ButtonV2 from "@Components/Button/ButtonV2";
import useAjax, { AjaxMethod } from "@Components/Hooks/useAjax";
import { Loading } from "@Components/Loading";
import { CommonEventType, MgmEventType, TelemetryEntity } from "@Components/Modules/Helpers";
import { formatPhoneNumber } from "@Components/Modules/Helpers/PhoneNumbersHelper";
import { TextboxAppearanceV2, TextboxV2 } from "@Components/Textbox";
import { Default } from "@Routes/MemberGetMemberLinks";
import * as stylex from "@stylexjs/stylex";

import * as css from "./MgmPhoneVerification.module.css";

export interface IMgmPhoneVerificationVerifyCode {
    description: string;
    details: string;
    placeholder: string;
    nextButtonText: string;
    prevButtonText: string;
    onSubmissionResponse: (isSuccess: boolean) => void;
    onClickBack: () => void;
    phoneNumber: string;
    resendSmsText: string;
    resendSmsButtonText: string;
    customErrorMessage: string;
    setCommunicationModal: (communicationModal: IUserCommunication) => void;
}

type FieldNames = {
    smsCode: string;
};

type SendConfirmationSmsResponseType = IServiceError & {};

const MgmPhoneVerificationVerifyCode: FC<IMgmPhoneVerificationVerifyCode> = ({
    description,
    details,
    nextButtonText,
    placeholder,
    onSubmissionResponse,
    onClickBack,
    phoneNumber,
    resendSmsText,
    resendSmsButtonText,
    prevButtonText,
    customErrorMessage,
    setCommunicationModal,
}) => {
    const {
        handleSubmit,
        register,
        formState: { errors },
        setError,
        clearErrors,
    } = useForm<FieldNames>({
        mode: "onChange",
    });

    useEffect(() => sendTelemetryEvent({ eventName: "cshbck-mgmSmsCodeInput", actionType: "View" }), []);

    const { isLoading, request } = useAjax<SendConfirmationSmsResponseType>({
        ajaxAction: Default.url,
        ajaxMethod: AjaxMethod.Post,
        success: ({ commonServiceOutput }) => {
            if (commonServiceOutput?.userCommunication) {
                setCommunicationModal(commonServiceOutput.userCommunication);
                return;
            }

            const resultStatus = commonServiceOutput?.resultStatus || ({} as IResultStatus);
            if (resultStatus.success) {
                onSubmissionResponse(true);
            } else if (resultStatus.errorMessage) {
                setError("smsCode", { type: "custom", message: resultStatus.errorMessage });
            }
        },
    });

    const { isLoading: confirmationSmsLoading, request: resendSmsRequest } =
        useAjax<SendConfirmationSmsResponseType>({
            ajaxAction: Default.url,
            ajaxMethod: AjaxMethod.Post,
            success: ({ commonServiceOutput }) => {
                if (commonServiceOutput?.resultStatus?.errorMessage) {
                    setError("smsCode", {
                        type: "custom",
                        message: commonServiceOutput.resultStatus.errorMessage,
                    });
                }
            },
        });

    const onSubmit = (fields: FieldNames) => {
        clearErrors();
        request({ action: "ValidateConfirmationSms" }, { ...fields, phoneNumber });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <h3
                className={`${css.description} ${stylex.props(styles.noMargin).className}`}
                dangerouslySetInnerHTML={{ __html: description }}
            />
            <p>{details.replace("{phoneNumber}", formatPhoneNumber(phoneNumber))}</p>
            <div className={css.inputWrapper}>
                <TextboxV2
                    placeholder={placeholder}
                    appearance={TextboxAppearanceV2.Round}
                    type="number"
                    {...register("smsCode", {
                        required: { value: true, message: customErrorMessage },
                    })}
                    errorMessage={errors.smsCode?.message}
                />
            </div>
            <div className={css.buttonWrapper}>
                <ButtonV2
                    appearance="transparentRoundMagentaBorderAppearance"
                    type="button"
                    onClick={() => {
                        onClickBack();
                        sendTelemetryEvent({
                            eventName: `${TelemetryEntity.Common}-${MgmEventType.SmsPageNumberCorrect}`,
                            actionType: "Click",
                        });
                    }}
                >
                    {prevButtonText}
                </ButtonV2>
                <ButtonV2
                    appearance="magentaRoundAppearance"
                    type="submit"
                    onClick={() =>
                        sendTelemetryEvent({
                            eventName: `${TelemetryEntity.Common}-${CommonEventType.next}`,
                            actionType: "Click",
                        })
                    }
                >
                    {nextButtonText}
                </ButtonV2>
            </div>
            <div className={`${css.buttonWrapper} ${css.resendInfo}`}>
                <p>{resendSmsText}</p>
                <ButtonV2
                    type="submit"
                    onClick={(event) => {
                        resendSmsRequest({ action: "SendConfirmationSms" }, { phoneNumber });
                        clearErrors();
                        sendTelemetryEvent({
                            eventName: `${TelemetryEntity.Common}-${MgmEventType.SmsCodeResend}`,
                            actionType: "Click",
                        });
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                >
                    {resendSmsButtonText}
                </ButtonV2>
            </div>
            {(isLoading || confirmationSmsLoading) && <Loading isModal />}
        </form>
    );
};

const styles = stylex.create({
    noMargin: {
        margin: 0,
    },
});

export default MgmPhoneVerificationVerifyCode;
